import * as R from "ramda";
import { Breadcrumb } from "antd";
import React, { useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Redirect, Route, Switch, withRouter, Link } from "react-router-dom";
import { compose } from "recompose";
import { useTranslation } from "react-i18next";

import Nav from "../../components/Nav";
import Analytics from "../../scenes/Analytics";
import {
  DESELECT_OPPORTUNITY,
  fetchOpportunity,
  FETCH_OPPORTUNITY
} from "../../store/actions/opportunity";
import Page from "../../components/display/Page";
import { getTeam } from "../../utils/teams";

import OpportunitySkeleton from "./OpportunitySkeleton";
import OpportunityCandidates from "./routes/OpportunityCandidates";
import OpportunityConfig from "./routes/OpportunityConfig";
import OpportunityDetails from "./routes/OpportunityDetails/";
import OpportunityProcess from "./routes/OpportunityProcess/";
import {
  LIKE_PERMISSION,
  LIKE_SCREENING_RESTRICTED_PERMISSION
} from "./routes/OpportunityUsers/permissions";
import OpportunityUsers from "./routes/OpportunityUsers";

import styled from "styled-components/macro";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .ant-breadcrumb {
    font-size: 12px;
  }
`;

const Content = styled.div`
  display: flex;
  margin-top: 24px;
  margin-left: 24px;
  margin-bottom: ${props => (props.marginBottom ? "0px" : "6px")};
`;

const Opportunity = ({
  history,
  location,
  match,
  fetchingOpportunityStatus,
  opportunity,
  user,
  team
}) => {
  const { t } = useTranslation();
  const [breadcrumb, setBreadcrumb] = useState("");
  const refreshInterval = useRef();
  const baseLinkUrl = `/oportunidades/${match.params.id}`;

  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(fetchOpportunity(match.params.id));
    return () => {
      dispatch(DESELECT_OPPORTUNITY.run());
    };
  }, [match.params.id, dispatch]);

  React.useEffect(() => {
    if (fetchingOpportunityStatus === "rejected") {
      history.push("/oportunidades");
      dispatch(FETCH_OPPORTUNITY.clearStatus());
    }
  }, [fetchingOpportunityStatus, dispatch, history]);

  React.useEffect(() => {
    const pathname = location.pathname;

    if (pathname.includes("/usuarios")) {
      setBreadcrumb(t("breadcrumbs:users"));
    } else if (pathname.includes("/proceso")) {
      setBreadcrumb(t("breadcrumbs:process"));
    } else if (pathname.includes("/candidatos")) {
      setBreadcrumb(t("breadcrumbs:candidates"));
    } else if (pathname.includes("/configuracion")) {
      setBreadcrumb(t("breadcrumbs:settings"));
    } else if (pathname.includes("/analytics")) {
      setBreadcrumb(t("breadcrumbs:analytics"));
    } else {
      setBreadcrumb(t("breadcrumbs:details"));
    }
  }, [location.pathname, t]);

  React.useEffect(() => {
    const pendingPost = R.pipe(
      R.path(["multiposting", "socialFeed"]),
      R.toPairs,
      R.map(R.tail),
      R.flatten,
      R.any(R.has("pending"))
    )(opportunity);

    if (pendingPost) {
      refreshInterval.current = setInterval(() => {
        dispatch(fetchOpportunity(match.params.id));
      }, 2500);
    }

    return () => {
      clearInterval(refreshInterval.current);
    };
  }, [dispatch, match.params.id, opportunity]);

  if (
    (["init", "pending"].includes(fetchingOpportunityStatus) &&
      !refreshInterval.current) ||
    opportunity.id !== match.params.id ||
    !opportunity.id
  ) {
    return <OpportunitySkeleton />;
  }

  const isOwner = R.propEq("id", opportunity.owner, user);
  const isAdmin = R.pipe(
    R.propOr([], "roles"),
    R.either(R.includes("admin"), R.includes("headadmin"))
  )(user);
  const permissions = R.propOr(
    [],
    "permissions",
    R.find(R.propEq("id", user.id), opportunity.guest || [])
  );
  const hasCandidateSelectionPermission =
    permissions.includes(LIKE_PERMISSION) ||
    permissions.includes(LIKE_SCREENING_RESTRICTED_PERMISSION);

  return (
    <Container>
      <Nav
        menuWidth="100%"
        path={match.url}
        links={[
          {
            testKey: "detalles",
            to: `${baseLinkUrl}/detalles`,
            icon: "edit",
            label: t("breadcrumbs:opportunity")
          },
          ...(isOwner || isAdmin
            ? [
                {
                  testKey: "usuarios",
                  to: `${baseLinkUrl}/usuarios`,
                  icon: "user",
                  label: t("breadcrumbs:users")
                }
              ]
            : []),
          {
            testKey: "candidatos",
            to: `${baseLinkUrl}/candidatos`,
            icon: "idcard",
            label: t("breadcrumbs:candidates")
          },
          {
            testKey: "proceso",
            to: `${baseLinkUrl}/proceso`,
            icon: "swap",
            label: t("breadcrumbs:process")
          },
          {
            testKey: "configuracion",
            to: `${baseLinkUrl}/configuracion`,
            icon: "setting",
            label: t("breadcrumbs:settings")
          },
          {
            testKey: "analytics",
            to: `${baseLinkUrl}/analytics`,
            icon: "line-chart",
            label: t("breadcrumbs:analytics")
          }
        ]}
      />
      <Page fullWidth={match.url.includes("/proceso")}>
        <Content marginBottom={match.url.includes("/candidatos")}>
          <Breadcrumb separator=">" data-test="breadcrum">
            <Breadcrumb.Item href="/oportunidades">
              {t("breadcrumbs:opportunities")}
            </Breadcrumb.Item>
            {team.name && (
              <Breadcrumb.Item>
                <Link to={`/equipos/${team.id}`}>{team.name}</Link>
              </Breadcrumb.Item>
            )}
            <Breadcrumb.Item>
              <Link to={`/oportunidades/${match.params.id}/detalles`}>
                {opportunity.title}
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <b>{breadcrumb}</b>
            </Breadcrumb.Item>
          </Breadcrumb>
        </Content>
      </Page>
      <Switch>
        <Route
          exact
          path={`${baseLinkUrl}/detalles/:section`}
          render={() => (
            <OpportunityDetails
              opportunity={opportunity}
              permissions={permissions}
              isAdmin={isAdmin}
              isOwner={isOwner}
            />
          )}
        />
        <Route
          exact
          path={`${baseLinkUrl}/detalles`}
          render={() => (
            <OpportunityDetails
              opportunity={opportunity}
              permissions={permissions}
              isAdmin={isAdmin}
              isOwner={isOwner}
            />
          )}
        />
        <Route
          path={`${baseLinkUrl}/proceso`}
          render={props => (
            <OpportunityProcess
              match={match}
              canChat={isOwner || isAdmin || permissions.includes("chat")}
              canMove={isOwner || isAdmin || hasCandidateSelectionPermission}
              {...props}
            />
          )}
        />
        <Route
          path={`${baseLinkUrl}/candidatos/:category?`}
          render={props => (
            <OpportunityCandidates
              canChat={isOwner || isAdmin || permissions.includes("chat")}
              canMove={isOwner || isAdmin || hasCandidateSelectionPermission}
              restrictAdvanceToProcessByScreening={
                !isOwner &&
                !isAdmin &&
                permissions.includes(LIKE_SCREENING_RESTRICTED_PERMISSION)
              }
              opportunity={opportunity}
              {...props}
            />
          )}
        />
        {(isOwner || isAdmin) && (
          <Route
            exact
            path={`${baseLinkUrl}/usuarios`}
            render={() => <OpportunityUsers />}
          />
        )}
        <Route
          exact
          path={`${baseLinkUrl}/configuracion`}
          render={props => (
            <OpportunityConfig
              permissions={permissions}
              isAdmin={isAdmin}
              isOwner={isOwner}
              {...props}
            />
          )}
        />
        <Route
          path={`${baseLinkUrl}/analytics`}
          render={() => <Analytics opportunity={opportunity} />}
        />
        <Redirect to={`${baseLinkUrl}/detalles`} />
      </Switch>
    </Container>
  );
};

export default compose(
  withRouter,
  connect(state => ({
    fetchingOpportunityStatus: FETCH_OPPORTUNITY.getStatus(state),
    opportunity: R.pathOr({}, ["opportunity", "selected"], state),
    user: state.user,
    team: getTeam(state)
  }))
)(Opportunity);
